/*
 * Copyright (c) 2022 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import { TermsAndConditionsService } from '../terms-and-conditions/service/terms-and-conditions.service';
import {
  TermsAndConditionsAgreementVoModel,
  TermsAndConditionsDtoModel,
  TermsAndConditionsTypeEnum
} from '@well/domain';
import { LoggedInUser, UserContext } from '@irian-ro/auth-shared-module';
import {
  TermsAndConditionsAgreementService
} from '../terms-and-conditions/service/terms-and-conditions-agreement.service';
import { of } from 'rxjs';
import { LocalStorageService } from 'ngx-webstorage';
import { translationSchema } from '@well/translation';
import { CrmFileStorageService } from '@well/resource-services';

@Component({
  selector: 'well-logged-in-terms-and-conditions-page',
  templateUrl: './logged-in-terms-and-conditions-page.component.html',
  styleUrls: ['./logged-in-terms-and-conditions-page.component.scss'],
})
export class LoggedInTermsAndConditionsPageComponent implements OnInit, OnDestroy {
  termsAndConditionsDto: TermsAndConditionsDtoModel | undefined = undefined;
  agreementVoModel: TermsAndConditionsAgreementVoModel | undefined = undefined;

  private loggedInUser: LoggedInUser | undefined = undefined;
  private termsAndConditionsType: TermsAndConditionsTypeEnum = TermsAndConditionsTypeEnum.PLATFORM;

  private unsubscribe: EventEmitter<boolean> = new EventEmitter();

  crmFileStorageService = inject(CrmFileStorageService);

  constructor(
    private userContext: UserContext,
    private localStorageService: LocalStorageService,
    private termsAndConditionsService: TermsAndConditionsService,
    private termsAndConditionsAgreementService: TermsAndConditionsAgreementService
  ) {}

  ngOnInit() {
    this.fetchTermsAndConditions();
  }

  ngOnDestroy() {
    this.unsubscribe.emit(true);
  }

  private fetchTermsAndConditions() {
    const authenticationToken: string = this.localStorageService.retrieve('authenticationToken');
    if (authenticationToken) {
      this.userContext
        .getLoggedInUser()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: (loggedInUser: any) => {
            if (loggedInUser && loggedInUser.authType) {
              this.loggedInUser = loggedInUser;
            }
            this.getTermsAndConditions();
          },
          error: () => {
            this.localStorageService.clear('authenticationToken');
            this.getTermsAndConditions();
          },
        });
    } else {
      this.getTermsAndConditions();
    }
  }

  private getTermsAndConditions() {
    this.termsAndConditionsService
      .getTermsAndConditions(this.termsAndConditionsType)
      .pipe(
        map((termsAndConditions) => termsAndConditions.data),
        switchMap((termsAndConditionsDto) => {
          this.termsAndConditionsDto = termsAndConditionsDto;
          if (this.loggedInUser?.uuid) {
            return this.termsAndConditionsAgreementService.getAgreementForUser(
              this.loggedInUser.uuid,
              termsAndConditionsDto.documentVersion
            );
          }

          return of(null);
        })
      )
      .subscribe((agreement) => {
        if (agreement) {
          this.agreementVoModel = agreement;
        }
      });
  }

  downloadPdf(attachmentUuid: string) {
    this.crmFileStorageService.downloadFileById(attachmentUuid);
  }

  protected readonly translationSchema = translationSchema;
}
